/**
 * 認証が必要なルートを保護するコンポーネント
 */
import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

/**
 * 認証保護されたルート
 * @param {Object} props - プロパティ
 * @param {React.ReactNode} props.children - 保護対象のコンポーネント
 * @param {string} props.redirectTo - リダイレクト先（デフォルト: /login）
 * @param {boolean} props.requireEmailVerified - メール認証必須かどうか
 * @returns {React.ReactNode} 認証済みの場合は子コンポーネント、未認証の場合はリダイレクト
 */
const ProtectedRoute = ({ 
  children, 
  redirectTo = '/login',
  requireEmailVerified = false 
}) => {
  const { currentUser, loading, isAuthenticated } = useAuth();

  // 認証状態のロード中は専用画面を表示
  if (loading) {
    return (
      <div style={loadingStyles.container}>
        <div style={loadingStyles.spinner}></div>
        <p style={loadingStyles.text}>認証状態を確認中...</p>
      </div>
    );
  }

  // ユーザーが認証されていない場合
  if (!isAuthenticated()) {
    return <Navigate to={redirectTo} replace />;
  }

  // メール認証が必要な場合のチェック
  if (requireEmailVerified && !currentUser.emailVerified) {
    return <Navigate to="/verify-email" replace />;
  }

  // 認証済みの場合は子コンポーネントを表示
  return children;
};

/**
 * ローディング画面のスタイル
 */
const loadingStyles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    backgroundColor: '#F0F8FF'
  },
  spinner: {
    width: '40px',
    height: '40px',
    border: '4px solid #f3f3f3',
    borderTop: '4px solid #007bff',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    marginBottom: '20px'
  },
  text: {
    color: '#333',
    fontSize: '16px',
    fontWeight: '500'
  }
};

// CSS アニメーション（インラインで追加）
const spinKeyframes = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

// スタイルタグをヘッドに追加（一度だけ）
if (typeof document !== 'undefined' && !document.querySelector('#protected-route-styles')) {
  const styleSheet = document.createElement('style');
  styleSheet.id = 'protected-route-styles';
  styleSheet.textContent = spinKeyframes;
  document.head.appendChild(styleSheet);
}

export default ProtectedRoute;